import { Handle, Position } from '@xyflow/react'
import { isFired } from './useFiredFlash'
import { OperandRow } from './OperandRow'
import { COMPARISON_ACCENT } from './operatorTheme'

/**
 * Comparison operators — one component for all six node types, keyed by `type`
 * (same pattern as MakeLiteralNode). Two value inputs (A, B) → a bool on
 * out-value. Each input is a pin OR an inline literal box (see OperandRow):
 * wire it, or type a value. `==` / `!=` are type-aware; `>` `<` `>=` `<=` order
 * numerically when both sides coerce to numbers, else lexicographically (see
 * src/lib/compareValues.js). The operator is the NODE TYPE, not editable config,
 * so there is no dropdown — pick the specific node from the palette.
 */

const OP = {
  equal:        { sym: '==', label: 'Equal' },
  notEqual:     { sym: '!=', label: 'Not Equal' },
  greater:      { sym: '>',  label: 'Greater' },
  less:         { sym: '<',  label: 'Less' },
  greaterEqual: { sym: '>=', label: 'Greater or Equal' },
  lessEqual:    { sym: '<=', label: 'Less or Equal' },
}

const ACCENT   = COMPARISON_ACCENT
const HEADER   = '#0a2824'
const WILDCARD = '#b0b0b0'   // any-type input pin
const BOOL     = '#d06060'   // bool output pin (Unreal convention)

export function ComparisonNode({ id, type, data, selected }) {
  const meta    = OP[type] ?? OP.equal
  const fired   = isFired(data)
  const preview = data._preview   // set by the runtime each time the node resolves

  return (
    <div style={{
      background: selected ? '#08201c' : '#04120f',
      border: `2px solid ${fired ? '#ffcc44' : selected ? ACCENT : '#1a4a44'}`,
      borderRadius: 6, minWidth: 190, fontFamily: 'var(--m-font)', fontSize: 12,
      boxShadow: fired ? '0 0 14px rgba(255,200,40,0.6)' : selected ? `0 0 12px ${ACCENT}33` : 'none',
    }}>
      <div style={{
        background: HEADER,
        padding: '7px 14px 7px 16px',
        display: 'flex', alignItems: 'center', gap: 8,
        borderBottom: `1px solid ${ACCENT}33`, borderRadius: '4px 4px 0 0',
        position: 'relative',
      }}>
        <span style={{ color: ACCENT, fontWeight: 'bold', fontSize: 13, fontFamily: 'var(--m-mono)' }}>{meta.sym}</span>
        <span style={{ color: ACCENT, fontWeight: 'bold', fontSize: 11, letterSpacing: 0.5 }}>{meta.label}</span>
        {/* Output (bool) pin — ReactFlow's default right-edge placement. To nudge
            it, add e.g. `right: N` to this style. Kept dead simple on purpose. */}
        <Handle type="source" position={Position.Right} id="out-value"
          style={{ background: BOOL, width: 10, height: 10, borderRadius: '50%' }} />
      </div>

      {['a', 'b'].map(pin => (
        <OperandRow key={pin}
          nodeId={id} pinId={`in-${pin}`} label={pin.toUpperCase()}
          accent={ACCENT} pinColor={WILDCARD} borderColor="#0e2420"
          value={data[pin]} onChange={v => data.onChange?.(pin, v)} />
      ))}

      {preview !== undefined && (
        <div style={{ padding: '5px 14px 7px 16px', color: preview ? '#7ad29a' : '#c08080', fontSize: 10 }}>
          → {String(preview)}
        </div>
      )}
    </div>
  )
}
